import React, { useState } from 'react';
import api from '../lib/api';
import { X, Check, AlertTriangle, KeyRound } from 'lucide-react';

interface ProfileMenuProps {
  user: { email: string; full_name: string; role: string };
  onClose: () => void;
}

export function ProfileMenu({ user, onClose }: ProfileMenuProps) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/auth/change-password', { current_password: currentPassword, new_password: newPassword });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to update password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-start justify-end p-6 bg-slate-900/10"
      data-testid="profile-menu-overlay"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mt-14 w-80 bg-white border border-slate-200 rounded-2xl shadow-2xl p-5 space-y-4"
        data-testid="profile-menu-panel"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-mono uppercase tracking-widest text-slate-400 font-bold">Your Profile</h3>
          <button onClick={onClose} data-testid="profile-menu-close-button" className="text-slate-400 hover:text-slate-700">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-1.5 text-xs bg-slate-50 rounded-xl p-3 border border-slate-100">
          <div className="flex justify-between gap-3">
            <span className="text-slate-400 font-semibold shrink-0">Name</span>
            <span className="text-slate-800 font-bold text-right">{user.full_name || '—'}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-slate-400 font-semibold shrink-0">Email</span>
            <span className="text-slate-800 font-bold text-right break-all">{user.email}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-slate-400 font-semibold shrink-0">Role</span>
            <span className="text-slate-800 font-bold text-right">{user.role}</span>
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4 space-y-3">
          <h4 className="text-[10px] font-mono uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5">
            <KeyRound className="h-3 w-3" /> Change Password
          </h4>
          <form onSubmit={handleSubmit} className="space-y-2.5">
            <input
              type="password"
              required
              data-testid="profile-current-password-input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Current password"
              className="w-full bg-slate-50 border border-slate-200 px-3 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 font-medium"
            />
            <input
              type="password"
              required
              data-testid="profile-new-password-input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password (min 8 chars)"
              className="w-full bg-slate-50 border border-slate-200 px-3 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 font-medium"
            />
            <input
              type="password"
              required
              data-testid="profile-confirm-password-input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className="w-full bg-slate-50 border border-slate-200 px-3 py-2 text-xs rounded-xl focus:outline-none focus:border-slate-400 font-medium"
            />

            {error && (
              <div data-testid="profile-password-error" className="p-2.5 bg-rose-50 border border-rose-100 rounded-xl text-rose-700 flex items-center gap-1.5 text-[11px] font-medium">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {error}
              </div>
            )}
            {success && (
              <div data-testid="profile-password-success" className="p-2.5 bg-emerald-50 border border-emerald-100 rounded-xl text-emerald-700 flex items-center gap-1.5 text-[11px] font-medium">
                <Check className="h-3.5 w-3.5 shrink-0" /> Password updated successfully.
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="profile-change-password-submit"
              className="w-full bg-slate-800 hover:bg-slate-950 text-white font-semibold text-xs py-2 px-4 rounded-xl transition-all duration-300 disabled:opacity-50"
            >
              {submitting ? 'Updating…' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
